import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/**
 * Main library class.
 *
 * @author Cristiam Mercado
 * @since 2.0.0
 * @version 6.0.0
 */
export class NgEventBus {
  /**
   * Main observable to multicast to all observers.
   */
  private eventBus: Subject<IEventBusMessage>;

  /**
   * Key message separator.
   */
  private separator = ':';

  /**
   * Constructor for this class: Initializes event bus.
   */
  constructor() {
    this.eventBus = new Subject<IEventBusMessage>();
  }

  /**
   * Validates key matching.
   *
   * @param key Key to identify the message/event.
   * @param wildcard Wildcard received from on method.
   *
   * @return true if key matches, false otherwise.
   */
  public keyMatch(key: string, wildcard: string): boolean {
    const w = '*';
    const ww = '**';

    const partMatch = (wl: string, k: string): boolean => wl === w || wl === k;

    const sep: string = this.separator;
    const kArr: string[] = key.split(sep);
    const wArr: string[] = wildcard.split(sep);

    const kLen: number = kArr.length;
    const wLen: number = wArr.length;
    const max: number = Math.max(kLen, wLen);

    for (let i = 0; i < max; i++) {
      const cK: string = kArr[i];
      const cW: string = wArr[i];

      if (cW === ww && typeof cK !== 'undefined') {
        return true;
      }

      if (!partMatch(cW, cK)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Publish a message/event to event bus.
   *
   * @param  key Key to identify the message/event.
   * @param  [data] Optional: Additional data sent with the message/event.
   * @throws {Error} key parameter must be a string and must not be empty.
   */
  public cast<T>(key: string, data?: T): void {
    if (!key.trim().length) {
      throw new Error('key parameter must be a string and must not be empty');
    }

    const metadata: MetaData<T> = new MetaData<T>(key, data);

    this.eventBus.next({ key, metadata });
  }

  /**
   * Returns an observable you can subscribe to listen messages/events.
   *
   * @param key Key to identify the message/event.
   *
   * @return Observable you can subscribe to listen messages/events.
   */
  public on<T>(key: string): Observable<MetaData<T>> {
    return this.eventBus.asObservable().pipe(
      filter((event: IEventBusMessage): boolean => this.keyMatch(event.key, key)),
      map((event: IEventBusMessage): MetaData<T> => event.metadata),
    );
  }
}

/**
 * Interface of the messages sent through the events bus.
 *
 * @author Cristiam Mercado
 * @since 2.0.0
 * @version 6.0.0
 */
export interface IEventBusMessage {
  /**
   * Key to identify message.
   */
  key: string;

  /**
   * Full message metadata.
   */
  metadata: MetaData;
}

/* tslint:disable:variable-name */

/**
 * Metadata of the messages sent through the events bus.
 *
 * @author Cristiam Mercado
 * @since 2.0.0
 * @version 6.0.0
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class MetaData<T = any> {
  /**
   * A unique identifier of the message sent through the events bus.
   * @private
   */
  private readonly _id: string;

  /**
   * Original key associated to the message.
   * @private
   */
  private readonly _key: string;

  /**
   * Data associated to message. It's optional.
   * @private
   */
  private readonly _data?: T;

  /**
   * Time in milliseconds in which the message was generated.
   * @private
   */
  private readonly _timestamp: number;

  /**
   * Constructor for this class.
   *
   * @param key Original key associated to the message sent through the events bus.
   * @param [data] Optional: Additional data sent with the message.
   */
  constructor(key: string, data?: T) {
    this._id = this.uuid();
    this._key = key;
    this._data = data;
    this._timestamp = new Date().getTime();
  }

  /**
   * Gets unique identifier of the message sent through the events bus.
   */
  public get id(): string {
    return this._id;
  }

  /**
   * Original key associated to the message.
   */
  public get key(): string {
    return this._key;
  }

  /**
   * Data associated to message. It's optional.
   */
  public get data(): T | undefined {
    return this._data;
  }

  /**
   * Gets the time in milliseconds in which the message was generated.
   */
  public get timestamp(): number {
    return this._timestamp;
  }

  /**
   * Generates UUID version 4. The solution above uses Math.random() for brevity, however Math.random() is not
   * guaranteed to be a high-quality RNG.
   *
   * @return UUID version 4.
   */
  private uuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string) => {
      const r: number = (Math.random() * 16) | 0;
      const v: number = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
